import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SpotlightRate {
  advertised_rate: number;
  comparison_rate: number | null;
  updated_at: string;
}

export function useSpotlightRate(spotlightName: string) {
  return useQuery({
    queryKey: ["spotlight-rate", spotlightName],
    queryFn: async (): Promise<SpotlightRate | null> => {
      // 1. Fetch the spotlight config
      const { data: spotlight, error: sErr } = await supabase
        .from("rate_spotlights")
        .select("*")
        .eq("name", spotlightName)
        .maybeSingle();

      if (sErr || !spotlight) return null;

      // 2. If linked to a specific product, fetch directly
      const linkedId = (spotlight as any).linked_product_id;
      if (linkedId) {
        const { data, error } = await supabase
          .from("loan_products")
          .select("advertised_rate, comparison_rate, updated_at")
          .eq("id", linkedId)
          .eq("is_live", true)
          .maybeSingle();

        if (error || !data) return null;
        return data as SpotlightRate;
      }

      // 3. Otherwise use filter criteria to find lowest rate
      let query = supabase
        .from("loan_products")
        .select("advertised_rate, comparison_rate, updated_at")
        .eq("loan_category", spotlight.loan_category)
        .eq("is_live", true);

      if (spotlight.loan_type) {
        query = query.eq("loan_type", spotlight.loan_type);
      }

      const includes = spotlight.include_purposes as string[] | null;
      if (includes && includes.length > 0) {
        const orClauses = includes
          .map((p) => `purpose.eq.${p},additional_purposes.cs.{${p}}`)
          .join(",");
        query = query.or(orClauses);
      }

      const excludes = spotlight.exclude_purposes as string[] | null;
      if (excludes && excludes.length > 0) {
        for (const ex of excludes) {
          query = query.not("purpose", "eq", ex);
          query = query.not("additional_purposes", "cs", `{${ex}}`);
        }
      }

      const { data, error } = await query
        .order("advertised_rate", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) return null;
      return data as SpotlightRate;
    },
    staleTime: 1000 * 60 * 60,
  });
}
